import type { TFunction } from 'i18next'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Database,
  Download,
  Eye,
  GitBranch,
  Loader2,
  Lock,
  Pencil,
  Pin,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageShell } from '@/components/layout/PageShell'
import { Button } from '@/components/ui/button'
import { DropdownMenuRadioGroup, DropdownMenuRadioItem } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { caseOwnedVersions } from '@/features/schema/case-binding'
import { groupSchemasByName } from '@/features/schema/format'
import {
  type SchemaEditorContext,
  useSchemaManager,
} from '@/features/schema/hooks/useSchemaManager'
import {
  useSchemaValidation,
  useValidateSavedSchema,
} from '@/features/schema/hooks/useSchemaValidation'
import type { SchemaGroup } from '@/features/schema/types'
import { useSchemaAgentIntro } from '@/features/schema-agent/hooks/use-schema-agent-intro'
import {
  hasWorkbenchContent,
  needsAnalyst,
  useCurrentSchemaSession,
} from '@/features/schema-agent/hooks/use-schema-session'
import { SchemaAgentButton } from '@/features/schema-agent/SchemaAgentButton'
import { SchemaAgentIntro } from '@/features/schema-agent/SchemaAgentIntro'
import { SchemaAgentSheet } from '@/features/schema-agent/SchemaAgentSheet'
import { SchemaSessionWorkbench } from '@/features/schema-agent/SchemaSessionWorkbench'
import type { Schema } from '@/services/api/schemas'
import { SchemaAgentDraftNotice } from './agent/SchemaAgentDraftNotice'
import { CaseSchemaIntro } from './CaseSchemaIntro'
import { ChangeStartingPointDialog } from './ChangeStartingPointDialog'
import { CreateCaseSchemaDialog } from './CreateCaseSchemaDialog'
import { DeleteSchemaDialog } from './DeleteSchemaDialog'
import { EditSchemaDescriptionDialog } from './EditSchemaDescriptionDialog'
import { ForkSchemaDialog } from './ForkSchemaDialog'
import { GalleryCard } from './GalleryCard'
import { ImportSchemaDialog } from './ImportSchemaDialog'
import { ListEmptyState } from './ListEmptyState'
import { PinVersionDialog } from './PinVersionDialog'
import { PHONE_TAP_HALO } from './phone-classes'
import { SchemaEditorBody } from './SchemaEditorBody'
import { SchemaElementDialogs } from './SchemaElementDialogs'
import { SchemaExportMenu, SchemaExportSubMenu } from './SchemaExportMenu'
import { type SchemaHeaderAction, SchemaHeaderSubMenu } from './SchemaHeaderActions'
import { IconTile, SchemaHero } from './SchemaHero'
import { SchemaListEmptyState } from './SchemaListEmptyState'

/**
 * Admin extraction-schema page — Option C "gallery → focused editor" layout
 * (PRD SC-006). A card gallery indexes the schemas; opening one drills into a
 * full-width editor whose heading is the schema name, with a breadcrumb back.
 * All create/update/delete/import/version flows stay in `useSchemaManager`; this
 * component is the presentational shell that wires the gallery and editor to it.
 */
export function SchemaManager({ context }: { context?: SchemaEditorContext } = {}) {
  const { t } = useTranslation()
  const sm = useSchemaManager(context)
  const validation = useSchemaValidation()
  useValidateSavedSchema(sm.lastSavedSchema, validation.validate)
  const [openId, setOpenId] = useState<string | null>(null)
  const [createMode, setCreateMode] = useState<'manual' | 'agent' | null>(null)
  // Case editor: the version pending a "use for extraction" confirm, and whether
  // the description editor is open.
  const [pinTarget, setPinTarget] = useState<Schema | null>(null)
  const [descOpen, setDescOpen] = useState(false)
  // Case editor: the destructive "start over from another schema" confirm.
  const [startingPointOpen, setStartingPointOpen] = useState(false)
  // Schema agent (PRD-0025): the side panel, and whether the body shows the
  // agent's draft read-only instead of the case's current schema.
  const [agentOpen, setAgentOpen] = useState(false)
  const [viewingDraft, setViewingDraft] = useState(false)
  const [startNewSession, setStartNewSession] = useState(false)
  const agentIntro = useSchemaAgentIntro(!!sm.caseId && !sm.loading)
  const { data: schemaSession } = useCurrentSchemaSession(sm.caseId ?? '')
  // The workbench takes the page over while its content needs the analyst; once
  // accepted and idle, the editor is home again and the agent stays a click away.
  const workbenchHasContent = hasWorkbenchContent(schemaSession)
  const awaitsReview = needsAnalyst(schemaSession)
  useEffect(() => {
    if (awaitsReview) {
      setViewingDraft(true)
      setAgentOpen(false)
    }
  }, [awaitsReview])
  const openAgent = () => {
    if (workbenchHasContent) setViewingDraft(true)
    else if (!sm.caseOwnsSchema) setCreateMode('agent')
    else setAgentOpen(true)
  }
  const agentDraft = viewingDraft ? (schemaSession?.draft ?? null) : null

  const groups = useMemo(() => groupSchemasByName(sm.schemas), [sm.schemas])
  const open = (id: string) => {
    const pendingSelection = sm.selectSchema(id)
    setOpenId(id)
    // Switching schema (incl. a version switch) invalidates the detail pane —
    // its selection points at a child of the schema we're leaving.
    sm.setSelectedEntityForDetail(null)
    sm.setSelectedRelationForDetail(null)
    sm.setSelectedPatternForDetail(null)
    return pendingSelection
  }
  // Acceptance creates a new immutable version outside this manager and binds
  // the case to it. Refresh the list/binding and open that version (independent
  // reads, so together), then leave the workbench: accepting ends the review.
  const syncAcceptedSchema = async (schemaId: string) => {
    await Promise.all([sm.refresh(), open(schemaId)])
    setViewingDraft(false)
  }

  const caseMode = !!sm.caseId
  const schema = sm.selectedSchema

  // Findings belong to the schema/version they were validated against — drop them
  // when the viewed schema changes (version switch, opening another, back to gallery).
  const { reset: resetValidation } = validation
  useEffect(() => {
    resetValidation()
  }, [schema?.id, resetValidation])

  // Case mode: the case's own versions (one schema, many versions). The editor
  // always edits the latest (head); older versions are read-only snapshots.
  const caseVersions = useMemo(
    () => (caseMode ? caseOwnedVersions(sm.schemas) : []),
    [caseMode, sm.schemas]
  )
  const latestCaseVersion = caseVersions[0] ?? null
  // Workspace (global) schemas — the starting points a case can build on, both
  // when it has no schema yet and when it starts over from a different one.
  const globalGroups = useMemo(() => groups.filter(g => g.versions[0]?.case_id == null), [groups])
  const inUseId = caseMode ? (sm.caseBindingSchemaId ?? latestCaseVersion?.id ?? null) : null
  const inUseSchema = caseVersions.find(v => v.id === inUseId) ?? null
  const inUseLabel = inUseSchema ? `v${inUseSchema.version}` : null
  // Already-processed versions that differ from the one extraction now uses.
  const staleVersions = [...sm.processedVersions].filter(v => v !== inUseLabel).sort()

  if (sm.loading) return <SchemaLoadingScreen />

  let hero: ReactNode = null
  let toolbar: ReactNode
  let body: ReactNode = null

  if (caseMode) {
    const caseReady = sm.caseOwnsSchema && schema != null && schema.case_id != null
    const viewingIsLatest = caseReady && schema.id === latestCaseVersion?.id
    // The header follows the case's own schema; the body follows the state —
    // intro, loading, editor, or the agent's read-only draft (PRD-0025).
    hero = caseReady ? (
      <CaseSchemaEditorHeader
        schema={schema}
        versions={caseVersions}
        latestId={latestCaseVersion?.id ?? null}
        inUseId={inUseId}
        onChangeVersion={open}
        onPin={() => setPinTarget(schema)}
        onEditDescription={() => setDescOpen(true)}
        onChangeStartingPoint={() => setStartingPointOpen(true)}
        onValidate={() => validation.validate(schema as unknown as Record<string, unknown>)}
        validating={validation.validating}
      />
    ) : (
      <CaseSchemaHeader subtitle="Define how this case extracts entities" />
    )
    if (agentDraft) {
      body = (
        <div className="space-y-4">
          <SchemaAgentDraftNotice onOpenReview={openAgent} onBack={() => setViewingDraft(false)} />
          <SchemaEditorBody
            sm={sm}
            schema={agentDraft}
            validation={validation}
            editing={{ readOnly: true }}
          />
        </div>
      )
    } else if (!sm.caseOwnsSchema) {
      body = (
        <CaseSchemaIntro
          caseName={context?.caseName}
          globalDefault={sm.schemas.find(s => s.is_active) ?? null}
          globalGroups={globalGroups}
          actions={{
            startFromGlobal: async (id, name) => {
              const newId = await sm.startFromGlobal(id, name)
              if (newId) open(newId)
            },
            createBlank: () => setCreateMode('manual'),
            createWithAgent: openAgent,
          }}
        />
      )
    } else if (caseReady) {
      body = (
        <div className="space-y-4">
          {staleVersions.length > 0 && (
            <ReprocessNotice versions={staleVersions} inUseLabel={inUseLabel} />
          )}
          {!viewingIsLatest && latestCaseVersion && (
            <ReadOnlyVersionNotice
              viewing={schema.version}
              latest={latestCaseVersion.version}
              onGoToLatest={() => open(latestCaseVersion.id)}
            />
          )}
          <SchemaEditorBody
            sm={sm}
            schema={schema}
            validation={validation}
            editing={{
              readOnly: !viewingIsLatest,
              toolbarEnd: <SchemaAgentButton onClick={openAgent} />,
            }}
          />
        </div>
      )
    } else {
      body = <EditorLoadingPane />
    }
  } else {
    const editing = openId !== null
    const ready = editing && schema !== null && schema.id === openId
    const versions = ready ? (groups.find(g => g.name === schema.name)?.versions ?? [schema]) : []
    hero = ready ? (
      <SchemaEditorHeader
        schema={schema}
        versions={versions}
        onChangeVersion={open}
        onBack={() => setOpenId(null)}
        onNewVersion={() => sm.createNewVersion(schema.id)}
        onActivate={() => sm.activateSchema(schema.id)}
        onDelete={() => sm.setDeleteTarget(schema)}
        onImport={() => sm.setImportDialogOpen(true)}
        onValidate={() => validation.validate(schema as unknown as Record<string, unknown>)}
        validating={validation.validating}
      />
    ) : (
      <GalleryHeader count={groups.length} summary={t('data.schema.summary')} />
    )
    toolbar = editing ? undefined : (
      <Button
        variant="outline"
        size="sm"
        onClick={() => sm.setImportDialogOpen(true)}
        className="border-border"
      >
        <Upload className="h-4 w-4 mr-1" />
        Import JSON
      </Button>
    )
    body = editing ? (
      ready ? (
        <SchemaEditorBody sm={sm} schema={schema} validation={validation} />
      ) : (
        <EditorLoadingPane />
      )
    ) : (
      <SchemaGalleryGrid groups={groups} onOpen={open} onDelete={sm.setDeleteTarget} />
    )
  }

  if (caseMode && viewingDraft && schemaSession && workbenchHasContent) {
    return (
      <PageShell layout="full">
        <SchemaAgentIntro
          open={agentIntro.open}
          onDismiss={agentIntro.dismiss}
          onTryNow={openAgent}
        />
        <SchemaSessionWorkbench
          session={schemaSession}
          navigation={{
            back: () => setViewingDraft(false),
            newSession: () => {
              setViewingDraft(false)
              setStartNewSession(true)
              setAgentOpen(true)
            },
            accepted: syncAcceptedSchema,
          }}
        />
      </PageShell>
    )
  }

  return (
    <PageShell layout="padded" hero={hero} toolbar={toolbar}>
      <SchemaAgentIntro
        open={agentIntro.open}
        onDismiss={agentIntro.dismiss}
        onTryNow={openAgent}
      />
      {body}

      {caseMode && sm.caseId && (
        <SchemaAgentSheet
          caseId={sm.caseId}
          startNew={startNewSession}
          onStarted={() => setStartNewSession(false)}
          open={agentOpen}
          onOpenChange={setAgentOpen}
          caseOwnsSchema={sm.caseOwnsSchema}
          viewingDraft={viewingDraft}
          onViewDraft={view => {
            setViewingDraft(view)
            if (view) setAgentOpen(false)
          }}
          onAccepted={syncAcceptedSchema}
        />
      )}

      <CreateCaseSchemaDialog
        open={createMode !== null}
        mode={createMode ?? 'manual'}
        onOpenChange={next => !next && setCreateMode(null)}
        onCreate={async name => {
          const id = await sm.createCaseSchema(name)
          if (!id) return false
          await open(id)
          if (createMode === 'agent') setAgentOpen(true)
          return true
        }}
      />

      {caseMode && latestCaseVersion && (
        <ChangeStartingPointDialog
          open={startingPointOpen}
          onOpenChange={setStartingPointOpen}
          current={{ schema: latestCaseVersion, versionCount: caseVersions.length }}
          globalGroups={globalGroups}
          caseName={context?.caseName}
          onConfirm={async (sourceId, name) => {
            const id = await sm.replaceCaseSchema(sourceId, name)
            if (id) {
              setStartingPointOpen(false)
              open(id)
            }
          }}
        />
      )}

      <ForkSchemaDialog
        open={sm.forkDialogOpen}
        onOpenChange={sm.setForkDialogOpen}
        defaultName={sm.forkName}
        onConfirm={async name => {
          // On success, open the fork's detail (it replaces the global in view).
          const id = await sm.confirmFork(name)
          if (id) open(id)
        }}
      />

      <SchemaElementDialogs sm={sm} schema={schema} />

      <ImportSchemaDialog
        open={sm.importDialogOpen}
        onOpenChange={sm.setImportDialogOpen}
        onImport={sm.importSchema}
      />

      <DeleteSchemaDialog
        target={sm.deleteTarget}
        onOpenChange={() => sm.setDeleteTarget(null)}
        onConfirm={sm.deleteSchema}
        inUse={caseMode && sm.deleteTarget != null && inUseId === sm.deleteTarget.id}
      />

      <PinVersionDialog
        target={pinTarget}
        staleVersions={
          pinTarget
            ? [...sm.processedVersions].filter(v => v !== `v${pinTarget.version}`).sort()
            : []
        }
        onOpenChange={pinOpen => !pinOpen && setPinTarget(null)}
        onConfirm={async () => {
          if (pinTarget) await sm.setCaseBinding(pinTarget.id)
          setPinTarget(null)
        }}
      />

      <EditSchemaDescriptionDialog
        open={descOpen}
        initial={schema?.description ?? ''}
        onOpenChange={setDescOpen}
        onSave={sm.setCaseDescription}
      />
    </PageShell>
  )
}

// ==================== Loading / case headers ====================

function SchemaLoadingScreen() {
  return (
    <div className="flex items-center justify-center min-h-[60dvh] md:min-h-screen bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground text-sm">Loading schema…</p>
      </div>
    </div>
  )
}

function EditorLoadingPane() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )
}

/** Plain page header for the case schema intro / loading states. */
function CaseSchemaHeader({ subtitle }: { subtitle: string }) {
  return (
    <div className="flex items-start gap-4">
      <IconTile icon={Database} />
      <div className="flex-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Case schema</h1>
        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
      </div>
    </div>
  )
}

/** The "Validate schema" action shared by the case and admin editor headers. */
function ValidateButton({
  onValidate,
  validating,
}: {
  onValidate: () => void
  validating: boolean
}) {
  const { t } = useTranslation()
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onValidate}
      disabled={validating}
      className="h-8 border-border"
    >
      {validating ? (
        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
      ) : (
        <ShieldCheck className="mr-1 h-4 w-4" />
      )}
      {t('schemaAgent.header.validate')}
    </Button>
  )
}

/** Version dropdown shared by the case and admin editor headers; `suffix`
 * annotates each option (e.g. ` · latest` / ` · active`). */
function VersionSwitcher({
  schema,
  versions,
  onChange,
  suffix,
}: {
  schema: Schema
  versions: Schema[]
  onChange: (id: string) => void
  suffix: (v: Schema) => string
}) {
  return (
    <Select value={schema.id} onValueChange={onChange}>
      <SelectTrigger className="h-8 border-border bg-muted">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-card border-border">
        {versions.map(v => (
          <SelectItem key={v.id} value={v.id}>
            v{v.version}
            {suffix(v)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Validate — identical in both headers. */
function validateAction({
  onValidate,
  validating,
  t,
}: {
  onValidate: () => void
  validating: boolean
  t: TFunction
}): SchemaHeaderAction {
  return {
    id: 'validate',
    label: t('schemaAgent.header.validate'),
    icon: ShieldCheck,
    onSelect: onValidate,
    disabled: validating,
    expanded: <ValidateButton onValidate={onValidate} validating={validating} />,
  }
}

/** Export — identical in both headers. */
function exportAction({ schema, t }: { schema: Schema; t: TFunction }): SchemaHeaderAction {
  return {
    id: 'export',
    label: t('schemaAgent.header.export'),
    icon: Download,
    expanded: <SchemaExportMenu schema={schema} />,
    collapsed: <SchemaExportSubMenu schema={schema} />,
  }
}

/** Version picker — the two headers differ only in how they annotate a version. */
function versionAction({
  schema,
  versions,
  onChange,
  suffix,
  t,
}: {
  schema: Schema
  versions: Schema[]
  onChange: (id: string) => void
  suffix: (v: Schema) => string
  t: TFunction
}): SchemaHeaderAction {
  const label = t('schemaAgent.header.versionValue', { version: schema.version })
  return {
    id: 'version',
    label,
    icon: GitBranch,
    expanded: (
      <VersionSwitcher schema={schema} versions={versions} onChange={onChange} suffix={suffix} />
    ),
    collapsed: (
      <VersionMenu
        schema={schema}
        versions={versions}
        onChange={onChange}
        suffix={suffix}
        label={label}
      />
    ),
  }
}

/** Version picker as a submenu — the collapsed twin of {@link VersionSwitcher},
 * for when the header's actions have folded into the overflow menu. */
function VersionMenu({
  schema,
  versions,
  onChange,
  suffix,
  label,
}: {
  schema: Schema
  versions: Schema[]
  onChange: (id: string) => void
  suffix: (v: Schema) => string
  label: string
}) {
  return (
    <SchemaHeaderSubMenu icon={GitBranch} label={label}>
      <DropdownMenuRadioGroup value={schema.id} onValueChange={onChange}>
        {versions.map(v => (
          <DropdownMenuRadioItem key={v.id} value={v.id}>
            v{v.version}
            {suffix(v)}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </SchemaHeaderSubMenu>
  )
}

// ==================== Case editor header (hero) ====================

/**
 * Editor hero for a case-owned schema (SP-385): the schema name, a version
 * switcher marking the latest (head) and the in-use-for-extraction version, and a
 * pin control. Unlike the admin header there is no gallery to go back to, no
 * global-active toggle, and no manual version/import/delete menu — versions are
 * created automatically on edit, and the case uses this schema exclusively.
 */
function CaseSchemaEditorHeader({
  schema,
  versions,
  latestId,
  inUseId,
  onChangeVersion,
  onPin,
  onEditDescription,
  onChangeStartingPoint,
  onValidate,
  validating,
}: {
  schema: Schema
  versions: Schema[]
  latestId: string | null
  inUseId: string | null
  onChangeVersion: (id: string) => void
  onPin: () => void
  onEditDescription: () => void
  onChangeStartingPoint: () => void
  onValidate: () => void
  validating: boolean
}) {
  const { t } = useTranslation()
  const inUse = versions.find(v => v.id === inUseId)
  const isLatest = schema.id === latestId
  const actions = caseHeaderActions({
    schema,
    versions,
    latestId,
    inUseId,
    onChangeVersion,
    onPin,
    onChangeStartingPoint,
    onValidate,
    validating,
    t,
  })
  return (
    <SchemaHero
      schema={schema}
      showStats={false}
      showDescription={false}
      leading={<IconTile icon={Lock} />}
      below={
        <div className="flex flex-col gap-2">
          {/* Description and its edit affordance share one text flow, so the
           * control trails the last line instead of floating beside a tall
           * paragraph. */}
          <div className="max-w-3xl text-sm text-muted-foreground">
            {schema.description ? (
              schema.description
            ) : (
              <span className="italic text-muted-foreground/60">No description yet</span>
            )}
            {isLatest && (
              <button
                type="button"
                onClick={onEditDescription}
                className={`ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground ${PHONE_TAP_HALO}`}
                aria-label={schema.description ? 'Edit description' : 'Add a description'}
              >
                {schema.description ? (
                  <>
                    <Pencil className="h-3 w-3" /> Edit
                  </>
                ) : (
                  <>
                    <Plus className="h-3 w-3" /> Add description
                  </>
                )}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-md border border-border bg-card/40 px-2 py-0.5">
              <Lock className="h-3 w-3" /> Case schema
            </span>
            <span>
              Extraction uses{' '}
              <span className="font-medium text-foreground tabular-nums">
                {inUse ? `v${inUse.version}` : '—'}
              </span>
              {isLatest ? ' · viewing the latest version' : ' · viewing an older version'}
            </span>
          </div>
        </div>
      }
      actions={actions}
    />
  )
}

/**
 * Keep case management in Actions; leave the version selector and extraction
 * status visible while there is room. On narrow screens everything folds in.
 */
function caseHeaderActions({
  schema,
  versions,
  latestId,
  inUseId,
  onChangeVersion,
  onPin,
  onChangeStartingPoint,
  onValidate,
  validating,
  t,
}: {
  schema: Schema
  versions: Schema[]
  latestId: string | null
  inUseId: string | null
  onChangeVersion: (id: string) => void
  onPin: () => void
  onChangeStartingPoint: () => void
  onValidate: () => void
  validating: boolean
  t: TFunction
}): SchemaHeaderAction[] {
  const isInUse = schema.id === inUseId
  const versionSuffix = (v: Schema) =>
    `${v.id === latestId ? ` · ${t('schemaAgent.header.latest')}` : ''}${
      v.id === inUseId ? ` · ${t('schemaAgent.header.inUseSuffix')}` : ''
    }`
  return [
    { ...validateAction({ onValidate, validating, t }), inMenu: true },
    { ...exportAction({ schema, t }), inMenu: true },
    {
      id: 'starting-point',
      inMenu: true,
      label: t('schemaAgent.header.changeStartingPoint'),
      icon: RotateCcw,
      onSelect: onChangeStartingPoint,
      hint: t('schemaAgent.header.changeStartingPointHint'),
    },
    versionAction({ schema, versions, onChange: onChangeVersion, suffix: versionSuffix, t }),
    isInUse
      ? {
          id: 'in-use',
          label: t('schemaAgent.header.inUse'),
          icon: Pin,
          disabled: true,
          expanded: (
            <span className="inline-flex h-8 items-center gap-1.5 rounded-md border border-success/40 bg-success/10 px-2.5 text-xs font-medium text-success-foreground">
              <Pin className="h-3.5 w-3.5" />
              {t('schemaAgent.header.inUse')}
            </span>
          ),
        }
      : {
          id: 'pin',
          label: t('schemaAgent.header.useThisVersion'),
          icon: Pin,
          onSelect: onPin,
          hint: t('schemaAgent.header.useThisVersionHint'),
        },
  ]
}

// ==================== Case editor notices ====================

/** Informational notice: already-processed files used a schema version other than
 * the one extraction now uses. Reprocessing is the user's call (Documents page). */
function ReprocessNotice({
  versions,
  inUseLabel,
}: {
  versions: string[]
  inUseLabel: string | null
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-warning-border bg-warning-surface p-4">
      <RefreshCw className="h-5 w-5 text-warning-foreground shrink-0 mt-0.5" />
      <div className="text-sm text-warning-foreground">
        <p className="font-medium">Some files were processed with {versions.join(', ')}.</p>
        <p className="mt-0.5 text-warning-foreground/90">
          This case now extracts with{' '}
          <span className="font-medium tabular-nums">{inUseLabel ?? '—'}</span>. Those documents
          keep their earlier results — re-run extraction on them from the Documents page if you want
          them on the current schema.
        </p>
      </div>
    </div>
  )
}

/** Notice shown when viewing a non-latest (read-only) version. */
function ReadOnlyVersionNotice({
  viewing,
  latest,
  onGoToLatest,
}: {
  viewing: number
  latest: number
  onGoToLatest: () => void
}) {
  return (
    <div className="flex max-md:flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/40 p-4">
      <Eye className="h-5 w-5 text-muted-foreground shrink-0" />
      <p className="max-md:min-w-48 flex-1 text-sm text-muted-foreground">
        You're viewing <span className="font-medium text-foreground tabular-nums">v{viewing}</span>{' '}
        (read-only). Edits always build from the latest version.
      </p>
      <Button variant="outline" size="sm" onClick={onGoToLatest} className="border-border shrink-0">
        <ArrowRight className="h-3.5 w-3.5 mr-1" />
        Go to v{latest}
      </Button>
    </div>
  )
}

// ==================== Gallery ====================

function GalleryHeader({ count, summary }: { count: number; summary: string }) {
  return (
    <div className="flex items-start gap-4">
      <div className="h-11 w-11 rounded-xl border border-primary/40 bg-primary/5 flex items-center justify-center shrink-0">
        <Database className="h-5 w-5 text-primary" />
      </div>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Extraction Schemas
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {count} schema{count === 1 ? '' : 's'} — {summary}
        </p>
      </div>
    </div>
  )
}

/** Admin schema gallery — a flat grid of schema cards. The case view uses the
 * editor-first {@link CaseSchemaIntro} flow instead (one schema per case). */
function SchemaGalleryGrid({
  groups,
  onOpen,
  onDelete,
}: {
  groups: SchemaGroup[]
  onOpen: (id: string) => void
  onDelete: (schema: Schema) => void
}) {
  const [query, setQuery] = useState('')
  const filtered = groups.filter(g => g.name.toLowerCase().includes(query.toLowerCase()))

  if (groups.length === 0) return <SchemaListEmptyState />

  return (
    <div className="flex flex-col gap-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Filter schemas..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="pl-9 bg-muted border-border"
        />
      </div>

      {filtered.length === 0 ? (
        <ListEmptyState icon={Database} message="No schemas match your filter" />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map(group => (
            <GalleryCard key={group.name} group={group} onOpen={onOpen} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  )
}

// ==================== Editor header (hero) ====================

function SchemaEditorHeader({
  schema,
  versions,
  onChangeVersion,
  onBack,
  onNewVersion,
  onActivate,
  onDelete,
  onImport,
  onValidate,
  validating,
}: {
  schema: Schema
  versions: Schema[]
  onChangeVersion: (id: string) => void
  onBack: () => void
  onNewVersion: () => void
  onActivate: () => void
  onDelete: () => void
  onImport: () => void
  onValidate: () => void
  validating: boolean
}) {
  // Admin tracks the global active version. The case view uses its own
  // {@link CaseSchemaEditorHeader} (in-use binding + pin, no gallery to go back to).
  const { t } = useTranslation()
  const actions = adminHeaderActions({
    schema,
    versions,
    onChangeVersion,
    onNewVersion,
    onActivate,
    onDelete,
    onImport,
    onValidate,
    validating,
    t,
  })
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={onBack}
          className={`hover:text-foreground max-md:shrink-0 ${PHONE_TAP_HALO}`}
        >
          Schemas
        </button>
        <ChevronRight className="h-3.5 w-3.5 max-md:shrink-0" />
        <span className="text-foreground max-md:min-w-0 max-md:truncate">{schema.name}</span>
      </div>
      <SchemaHero
        schema={schema}
        showStats={false}
        leading={
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="h-11 w-11 p-0 border-border shrink-0"
            title="Back to all schemas"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        }
        actions={actions}
      />
    </div>
  )
}

/**
 * The admin header's action cluster. Same two presentations as the case
 * header's; the version-lifecycle items stay in the overflow menu either way, so
 * the header never shows two ⋯ buttons.
 */
function adminHeaderActions({
  schema,
  versions,
  onChangeVersion,
  onNewVersion,
  onActivate,
  onDelete,
  onImport,
  onValidate,
  validating,
  t,
}: {
  schema: Schema
  versions: Schema[]
  onChangeVersion: (id: string) => void
  onNewVersion: () => void
  onActivate: () => void
  onDelete: () => void
  onImport: () => void
  onValidate: () => void
  validating: boolean
  t: TFunction
}): SchemaHeaderAction[] {
  const active = schema.is_active
  return [
    validateAction({ onValidate, validating, t }),
    exportAction({ schema, t }),
    versionAction({
      schema,
      versions,
      onChange: onChangeVersion,
      suffix: v => (v.is_active ? ` · ${t('schemaAgent.header.active')}` : ''),
      t,
    }),
    {
      // One-way: the off→on click makes this the global active version.
      id: 'active',
      label: t('schemaAgent.header.setActive'),
      icon: Check,
      onSelect: onActivate,
      disabled: active,
      expanded: (
        <div
          className="flex h-8 items-center gap-2 rounded-md border border-border bg-muted px-2.5"
          title={active ? t('schemaAgent.header.isActive') : t('schemaAgent.header.setActiveHint')}
        >
          <Switch
            size="sm"
            checked={active}
            onCheckedChange={checked => {
              if (checked) onActivate()
            }}
            aria-label={t('schemaAgent.header.setActive')}
          />
          <span
            className={
              active
                ? 'select-none text-xs font-medium text-success-foreground'
                : 'select-none text-xs text-muted-foreground'
            }
          >
            {t('schemaAgent.header.active')}
          </span>
        </div>
      ),
    },
    {
      id: 'new-version',
      label: t('schemaAgent.header.newVersion'),
      icon: Plus,
      onSelect: onNewVersion,
      inMenu: true,
    },
    {
      id: 'import',
      label: t('schemaAgent.header.importJson'),
      icon: Upload,
      onSelect: onImport,
      inMenu: true,
    },
    {
      id: 'delete',
      label: t('schemaAgent.header.deleteVersion'),
      icon: Trash2,
      onSelect: onDelete,
      inMenu: true,
      destructive: true,
    },
  ]
}

// ==================== Editor body (tabs + master/detail) ====================

/** Count badge on a section tab — primary-tinted when populated, muted at zero. */
